import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const ReviewAppraisal = () => {
  const { empCode, cycleId } = useParams();
  const navigate = useNavigate();
  const [goals, setGoals] = useState([]);
  const [compensation, setCompensation] = useState({ hike: '', bonus: '', justification: '' });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchEmployeeReview();
  }, []);

  const fetchEmployeeReview = async () => {
    try {
      const response = await fetch(`http://localhost:8000/api/manager/employee-review/${empCode}/${cycleId}`);
      const data = await response.json();
      setGoals(data.map(g => ({ ...g, managerRating: g.manager_rating || '', managerNote: g.manager_comments || '' })));
    } catch (error) {
      console.error('Error fetching employee review:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateGoal = (index, field, value) => {
    const updated = [...goals];
    updated[index][field] = value;
    setGoals(updated);
  };

  const handleSubmitReview = async () => {
    if (goals.some(g => !g.managerRating || !g.managerNote)) {
      alert('Please provide rating and comments for all KPIs');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        goals: goals.map(g => ({
          id: g.kpi_id,
          managerRating: g.managerRating,
          managerNote: g.managerNote
        }))
      };

      const response = await fetch(`http://localhost:8000/api/performance-review/submit-manager/${empCode}/${cycleId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        alert('Manager review submitted successfully!');
      } else {
        alert('Failed to submit review');
      }
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleProposeCompensation = async () => {
    if (!compensation.hike || !compensation.bonus) {
      alert('Please enter hike percentage and bonus amount');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        emp_code: empCode,
        cycle_id: parseInt(cycleId),
        proposed_increment_percent: parseFloat(compensation.hike),
        proposed_bonus: parseFloat(compensation.bonus),
        manager_justification: compensation.justification
      };

      const response = await fetch('http://localhost:8000/api/manager/compensation-proposal', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        alert('Compensation proposal submitted successfully!');
        navigate('/manager/appraisal/team');
      } else {
        alert('Failed to submit compensation proposal');
      }
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <button
        onClick={() => navigate('/manager/appraisal/team')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6"
      >
        <ArrowLeftIcon className="h-5 w-5" />
        Back to Team Appraisals
      </button>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Review Appraisal - {empCode}</h1>
        <p className="text-gray-600 mb-6">Review employee self-assessment and provide your ratings</p>

        <div className="space-y-6">
          {goals.map((goal, index) => (
            <div key={goal.kpi_id} className="border border-gray-200 rounded-lg p-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-800">{goal.kpi_title}</h3>
                <p className="text-sm text-gray-600 mt-1">{goal.kpi_description}</p>
                <p className="text-sm text-indigo-600 mt-1">Weightage: {goal.weightage}%</p>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg mb-4">
                <h4 className="font-medium text-blue-900 mb-2">Employee Self-Assessment</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-blue-700">Self Rating: <span className="font-bold">{goal.employee_rating || 'Not rated'}/5.0</span></p>
                  </div>
                  <div>
                    <p className="text-sm text-blue-700 font-medium">Comments:</p>
                    <p className="text-sm text-blue-900 mt-1">{goal.employee_comments || 'No comments'}</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Manager Review</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Manager Rating</label>
                    <select
                      value={goal.managerRating}
                      onChange={(e) => updateGoal(index, 'managerRating', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    >
                      <option value="">Select Rating</option>
                      {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}.0</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Manager Comments</label>
                    <textarea
                      value={goal.managerNote}
                      onChange={(e) => updateGoal(index, 'managerNote', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      rows="3"
                      placeholder="Provide your feedback..."
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSubmitReview}
            disabled={submitting}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {submitting ? 'Submitting...' : 'Submit Manager Review'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Propose Compensation</h2>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Hike (%)</label>
            <input
              type="number"
              value={compensation.hike}
              onChange={(e) => setCompensation({ ...compensation, hike: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="e.g., 12.5"
              step="0.1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Bonus Amount</label>
            <input
              type="number"
              value={compensation.bonus}
              onChange={(e) => setCompensation({ ...compensation, bonus: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="e.g., 2500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Justification</label>
            <textarea
              value={compensation.justification}
              onChange={(e) => setCompensation({ ...compensation, justification: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="Reason for proposed compensation..."
            />
          </div>
        </div>
        <div className="flex justify-end">
          <button
            onClick={handleProposeCompensation}
            disabled={submitting}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {submitting ? 'Submitting...' : 'Propose Compensation'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewAppraisal;
